import {
  ApplicationRef,
  ComponentRef,
  computed,
  createComponent,
  EnvironmentInjector,
  Injectable,
  Injector,
  Renderer2,
  RendererFactory2,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { OverlayService } from '@app/core/services/overlay.service';
import { ModalSize } from '@app/core/types/modal-size';
import { ModalConfig } from '@shared/interfaces/modal/modal-config';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { ModalStates } from '@app/core/types/modal-states';
import { AlertService } from '@app/core/services/alert.service';
import { Observable, Subject } from 'rxjs';
import { modalConfigDefault } from '@app/core/config/modal-config.default';
import { ModalAction } from '@shared/interfaces/modal/modal-action';

/**
 * A service to manage modal dialogs, allowing for dynamic modal creation, configuration, and cleanup.
 * Provides signal-based state management and integration with overlays and alerts.
 *
 * ```typescript
 * const modal = this.modalService.show({
 *   component: MyCustomComponent,
 *   title: 'Custom Modal',
 *   size: 'lg',
 *   actions: [
 *    { label: 'button.cancel', type: 'secondary', closeOnClick: true },
 *    { label: 'button.ok', type: 'primary', method: 'submit' },
 *   ],
 *   params: { key: 'value' },
 * });
 *
 * modal.subscribe(result => {
 *   console.log('Modal closed with:', result);
 * });
 *
 * // Later, update buttons dynamically
 * this.modalService.updateActions([
 *   { label: 'button.save', type: 'primary', method: 'save' }
 * ]);
 *
 * // Or update size
 * this.modalService.updateSize('xl');
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ModalService {
  /**
   * Internal signal to track the current state of the modal (e.g., open or closed).
   */
  private readonly modalStateSignal: WritableSignal<ModalStates | null> = signal(null);

  /**
   * Read-only signal providing access to the current modal state.
   */
  public readonly modalState: Signal<ModalStates | null> = computed(() => this.modalStateSignal());

  /**
   * Subject to handle modal results, allowing the modal to return data when closed.
   */
  private modalResultSubject: Subject<any> | null = null;

  /**
   * Specifies the default modal size. Can be overridden via configuration.
   */
  public size: ModalSize = 'base';

  /**
   * The container element in the DOM to hold the modal contents.
   */
  public container!: HTMLElement;

  /**
   * Reference to the dynamically created modal component.
   */
  public modalContainer?: ComponentRef<ModalComponent>;

  /**
   * Renderer instance to manage dynamic DOM modifications.
   */
  private renderer?: Renderer2;

  /**
   * Initializes the `ModalService` and injects all necessary dependencies.
   *
   * @param injector - Injector used to handle component dependencies.
   * @param appRef - Reference to the Angular application for attaching views.
   * @param envInjector - Environment injector for dependency resolution.
   * @param renderFactory - Factory for creating the `Renderer2` instance.
   * @param overlayService - Service for handling background overlays.
   * @param alertService - Service for displaying alerts, particularly error or status messages.
   */
  constructor(
    private readonly injector: Injector,
    private readonly appRef: ApplicationRef,
    private readonly envInjector: EnvironmentInjector,
    private readonly renderFactory: RendererFactory2,
    private readonly overlayService: OverlayService,
    private readonly alertService: AlertService,
  ) {}

  /**
   * Displays a modal with the provided configuration.
   *
   * @param modalConfig - Configuration for the modal, including the component, title, type, size, and optional parameters.
   * @returns An observable that emits a result when the modal is closed.
   */
  public show(modalConfig: ModalConfig): Observable<any> {
    this.modalResultSubject = new Subject<any>();

    /** Emit Modal Signal **/
    this.modalStateSignal.set('modal_opened');

    if (modalConfig.params) {
      modalConfig.params['__modalService'] = this;
    }

    /** Merge Modal Config with Defaults **/
    modalConfig = {
      ...modalConfigDefault,
      ...modalConfig,
    };

    /** Create and Attach Modal Container **/
    this.renderer = this.renderFactory.createRenderer(null, null);
    this.container = this.renderer.createElement('div');
    this.container.classList.add('modal-container', 'fixed', 'top-0', 'bottom-0', 'right-0', 'left-0', 'z-40', 'pointer-events-none');
    document.body.appendChild(this.container);

    /** Display Overlay **/
    this.overlayService.show();
    this.modalContainer = this.injectModal(modalConfig);

    /** Return Observable for Modal Actions **/
    return this.modalResultSubject.asObservable();
  }

  /**
   * Updates the modal actions (buttons) dynamically while the modal is open.
   *
   * @param actions - New array of modal actions to display
   *
   * @example
   * // Initially show cancel and next buttons
   * this.modalService.show({
   *   component: WizardComponent,
   *   actions: [
   *     { label: 'Cancel', type: 'secondary', closeOnClick: true },
   *     { label: 'Next', type: 'primary', method: 'next' }
   *   ]
   * });
   *
   * // Later, on the last step, change to cancel and finish
   * this.modalService.updateActions([
   *   { label: 'Cancel', type: 'secondary', closeOnClick: true },
   *   { label: 'Finish', type: 'primary', method: 'finish' }
   * ]);
   */
  public updateActions(actions: ModalAction[]): void {
    if (!this.modalContainer) {
      console.warn('No modal is currently open');
      return;
    }

    this.modalContainer.instance.modalConfig.actions = actions;
    this.modalContainer.changeDetectorRef.detectChanges();
  }

  /**
   * Updates the modal size dynamically while the modal is open.
   *
   * @param size - New modal size
   *
   * @example
   * // Start with a small modal
   * this.modalService.show({
   *   component: MyComponent,
   *   size: 'sm'
   * });
   *
   * // Later, expand to large
   * this.modalService.updateSize('lg');
   */
  public updateSize(size: ModalSize): void {
    if (!this.modalContainer) {
      console.warn('No modal is currently open');
      return;
    }

    this.modalContainer.instance.updateSize(size);
  }

  /**
   * Updates the modal title dynamically while the modal is open.
   *
   * @param title - New modal title (translation key or plain text)
   *
   * @example
   * this.modalService.updateTitle('Step 2 of 3');
   */
  public updateTitle(title: string): void {
    if (!this.modalContainer) {
      console.warn('No modal is currently open');
      return;
    }

    this.modalContainer.instance.modalConfig.title = title;
    this.modalContainer.changeDetectorRef.detectChanges();
  }

  /**
   * Closes the currently open modal and optionally emits a result.
   *
   * @param callbackParam - The result to pass back to observers when the modal is closed.
   * @param eventEmit - Flag indicating whether to emit an event when the modal is closed. Defaults to `true`.
   */
  public close(callbackParam?: any, eventEmit = true): void {
    /** Update Modal State **/
    this.modalStateSignal.set('modal_closed');

    if (this.modalResultSubject && eventEmit) {
      this.modalResultSubject.next(callbackParam);
      this.modalResultSubject.complete();
      this.modalResultSubject = null;
    }

    /** Hide Overlay **/
    this.overlayService.hide();

    /** Clean Up Modal **/
    setTimeout(() => {
      if (this.modalContainer) {
        this.alertService.close('modal');
        this.modalContainer.destroy();
        document.body.removeChild(this.container);
      }
    }, 300);
  }

  /**
   * Closes the modal without emitting a result.
   */
  public cancel(): void {
    this.close(null, false);
  }

  /**
   * Dynamically injects a modal component into the DOM based on the configuration provided.
   *
   * @param modalConfig - Configuration for the modal, including component and parameters.
   * @returns A reference to the dynamically created modal component.
   */
  private injectModal(modalConfig: ModalConfig): ComponentRef<ModalComponent> {
    const componentRef = createComponent(ModalComponent, {
      environmentInjector: this.envInjector,
      elementInjector: this.injector,
    });

    componentRef.instance.modalConfig = modalConfig;

    this.appRef.attachView(componentRef.hostView);
    this.renderer?.appendChild(this.container, componentRef.location.nativeElement);
    componentRef.changeDetectorRef.detectChanges();

    return componentRef;
  }
}
